import React, { useEffect, useRef, useState } from "react";
import { Checkbox, InputNumber, Tooltip } from "antd";
import { GenericCard } from "../../../../common/components/GenericCard";
import { Segmented } from "../../../../design-system";
import {
    AppstoreOutlined,
    BarChartOutlined,
    CloudOutlined,
    DownOutlined,
    HddOutlined,
    RightOutlined,
    SafetyOutlined,
    TeamOutlined,
    ToolOutlined,
    WifiOutlined,
} from "@ant-design/icons";
import { BudgetTabs } from "../layout/BudgetTabs";
import { useBudget } from "../BudgetContext";
import { getLines, groupBySegment, tailPoolByCategory } from "../model/api";
import { hashSeed } from "../model/forecast";
import { BudgetLine, CategoryKey, Segment, usd } from "../model/types";
import { formatCompact, formatPct } from "../model/format";
import classes from "./TrackView.module.less";

/** Quarters posted as actuals for the current FY (today is mid-FY). Past years
 * are fully posted (4), future years not at all (0). */
const MID_ELAPSED = 2;
const QUARTERS = [0, 1, 2, 3];
const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);

const ORDER: Segment[] = ["up_for_renewal", "new_spend", "tail", "under_contract"];

const CATEGORY_ICON: Record<CategoryKey, React.ReactNode> = {
    saas: <AppstoreOutlined />,
    infra_cloud: <CloudOutlined />,
    hardware: <HddOutlined />,
    telecom: <WifiOutlined />,
    prof_services: <TeamOutlined />,
    security: <SafetyOutlined />,
    data_analytics: <BarChartOutlined />,
    support_maint: <ToolOutlined />,
};
const CATEGORY_COLOR: Record<CategoryKey, string> = {
    saas: "#6366f1",
    infra_cloud: "#0ea5e9",
    hardware: "#64748b",
    telecom: "#14b8a6",
    prof_services: "#f59e0b",
    security: "#ef4444",
    data_analytics: "#8b5cf6",
    support_maint: "#10b981",
};

/** Where a line's actual comes from. */
type ActualKind = "close" | "usage" | "po" | "manual";
const SOURCE_LABEL: Record<ActualKind, string> = {
    close: "Closing price",
    usage: "Usage",
    po: "POs consumed",
    manual: "Actual",
};

/** Deterministic per-quarter variance, −8%…+8%. */
const variance = (id: string, q: number): number =>
    -0.08 + (hashSeed(`${id}:trk:${q}`) % 161) / 1000;

const phaseEven = (n: number): number[] => {
    const q = Math.round(n / 4);
    return [q, q, q, n - q * 3];
};

/** Quarter index (0–3) a contract closes on, from its "Q3 …" label. */
const closeQuarterIndex = (label?: string): number => {
    const m = label?.match(/Q(\d)/);
    return m ? Math.min(3, Math.max(0, parseInt(m[1], 10) - 1)) : 0;
};

interface TrackRow {
    id: string;
    logoUrl?: string;
    categoryKey?: CategoryKey;
    name: string;
    sub?: string;
    category: string;
    /** Target budget (net of expected savings) = the Budget tab's Target. */
    budget: number[];
    actual: (number | null)[];
    /** Baseline budget (before savings) = gross + correction. */
    baseline: number[];
    kind: ActualKind;
    editable: boolean;
}

interface TrackGroup {
    segment: Segment;
    label: string;
    rows: TrackRow[];
    budget: number[];
    actual: (number | null)[];
    baseline: number[];
}

const makeGroup = (
    segment: Segment,
    label: string,
    rows: TrackRow[],
    elapsed: number
): TrackGroup => ({
    segment,
    label,
    rows,
    budget: QUARTERS.map((q) => sum(rows.map((r) => r.budget[q]))),
    actual: QUARTERS.map((q) =>
        q >= elapsed ? null : sum(rows.map((r) => (r.actual[q] as number) ?? 0))
    ),
    baseline: QUARTERS.map((q) => sum(rows.map((r) => r.baseline[q]))),
});

const ALL_EXPANDED: Record<Segment, boolean> = {
    up_for_renewal: true,
    new_spend: true,
    tail: true,
    under_contract: true,
};

export const TrackView: React.FC = () => {
    const { workspace, year, corrections, tailTrendFor } = useBudget();
    const elapsed =
        year < workspace.currentYear
            ? 4
            : year > workspace.currentYear
            ? 0
            : MID_ELAPSED;
    // Quarters the table is filtered to. Clicking a card selects just that
    // quarter; the checkbox adds/removes more (mirrors the negotiations view).
    const [selectedQuarters, setSelectedQuarters] = useState<number[]>([
        Math.max(0, elapsed - 1),
    ]);
    const [checkedQuarters, setCheckedQuarters] = useState<number[]>([]);
    useEffect(() => {
        setSelectedQuarters([Math.max(0, elapsed - 1)]);
        setCheckedQuarters([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [year]);
    const selectQuarter = (q: number) => {
        setSelectedQuarters([q]);
        setCheckedQuarters([]);
    };
    const toggleQuarter = (q: number) => {
        setCheckedQuarters((prev) => {
            const base = prev.length === 0 ? [...selectedQuarters] : prev;
            const next = base.includes(q)
                ? base.filter((x) => x !== q)
                : [...base, q].sort();
            setSelectedQuarters(next.length > 0 ? next : [q]);
            return next;
        });
    };
    const [expanded, setExpanded] =
        useState<Record<Segment, boolean>>(ALL_EXPANDED);
    // User-entered actuals for manual new spend, keyed by `${lineId}:${q}`.
    const [manualActual, setManualActual] = useState<Record<string, number>>({});

    const categoryKeyOf = (categoryId: string): CategoryKey | undefined =>
        workspace.categories.find((c) => c.id === categoryId)?.key;

    const kindFor = (segment: Segment, line?: BudgetLine): ActualKind => {
        if (segment === "up_for_renewal") return "close";
        if (segment === "under_contract") return "usage";
        if (segment === "tail") return "po";
        return line?.contractId ? "close" : "manual";
    };

    const rowActual = (
        id: string,
        kind: ActualKind,
        budget: number[]
    ): (number | null)[] =>
        QUARTERS.map((q) => {
            if (q >= elapsed) return null;
            if (kind === "manual") return manualActual[`${id}:${q}`] ?? budget[q];
            return Math.round(budget[q] * (1 + variance(id, q)));
        });

    // Fold a Budget-tab correction into a line's phased budget, proportional to
    // its existing shape (so single-quarter lines take it all in that quarter).
    // Keyed by the same id the Budget tab corrects, so the two tabs stay in sync.
    const withCorrection = (id: string, budget: number[]): number[] => {
        const corr = corrections[id] ?? 0;
        if (!corr) return budget;
        const total = sum(budget);
        if (total <= 0) return budget;
        return budget.map((b) => Math.round(b + corr * (b / total)));
    };

    const buildGroups = (): TrackGroup[] => {
        const byKey: Partial<Record<Segment, TrackGroup>> = {};

        for (const g of groupBySegment(getLines(workspace, year))) {
            const rows: TrackRow[] = g.lines.map((l) => {
                const kind = kindFor(g.segment, l);
                const single = kind === "close" || kind === "manual";
                const qi = single ? closeQuarterIndex(l.closeQuarter) : 99;
                let budget: number[];
                let actual: (number | null)[];
                let baseline: number[];
                if (single) {
                    // Contracts and new spend land their whole value in the
                    // quarter they close / land on. Target budget nets the
                    // expected savings; baseline is the gross before savings.
                    const gross = l.planAmount.amount;
                    const expected = l.playbook?.savingsExpected.amount ?? 0;
                    budget = withCorrection(
                        l.id,
                        QUARTERS.map((q) => (q === qi ? Math.max(0, gross - expected) : 0))
                    );
                    baseline = withCorrection(
                        l.id,
                        QUARTERS.map((q) => (q === qi ? gross : 0))
                    );
                    // Actual = the signed price. Each deal captures a per-deal
                    // 35–90% of the expected savings, so the actual lands between
                    // the baseline (no savings) and the target (full expected).
                    const captured =
                        expected * (0.35 + (hashSeed(`${l.id}:cap`) % 56) / 100);
                    actual = QUARTERS.map((q) => {
                        if (q !== qi || q >= elapsed) return null;
                        return kind === "manual"
                            ? manualActual[`${l.id}:${qi}`] ?? gross
                            : Math.round(gross - captured);
                    });
                } else {
                    // No negotiated savings on these lines — baseline = budget.
                    budget = withCorrection(l.id, l.phasing);
                    baseline = budget;
                    actual = rowActual(l.id, kind, l.phasing);
                }
                return {
                    id: l.id,
                    logoUrl: l.logoUrl,
                    categoryKey: categoryKeyOf(l.categoryId),
                    name: l.label,
                    category: l.categoryName,
                    budget,
                    actual,
                    baseline,
                    kind,
                    editable: kind === "manual",
                };
            });
            byKey[g.segment] = makeGroup(g.segment, g.label, rows, elapsed);
        }

        const tailRows: TrackRow[] = tailPoolByCategory(workspace).map((sc) => {
            // Budgeted tail = run-rate grown by the same trend the Budget tab
            // applies; actuals track the run-rate.
            const base = phaseEven(sc.base);
            const budget = withCorrection(
                sc.categoryId,
                phaseEven(Math.round(sc.base * (1 + tailTrendFor(sc.categoryId))))
            );
            return {
                id: sc.categoryId,
                categoryKey: categoryKeyOf(sc.categoryId),
                name: sc.categoryName,
                sub: `${sc.vendorCount} vendors`,
                category: sc.categoryName,
                budget,
                actual: rowActual(sc.categoryId, "po", base),
                baseline: budget,
                kind: "po",
                editable: false,
            };
        });
        byKey.tail = makeGroup("tail", "Tail spend", tailRows, elapsed);

        return ORDER.map((seg) => byKey[seg]).filter(
            (g): g is TrackGroup => g !== undefined && g.rows.length > 0
        );
    };

    const groups = buildGroups();

    // Year roll-up for the chart and the table's grand-total row.
    const totalBudget = QUARTERS.map((q) => sum(groups.map((g) => g.budget[q])));
    const totalActual: (number | null)[] = QUARTERS.map((q) =>
        q >= elapsed ? null : sum(groups.map((g) => (g.actual[q] as number) ?? 0))
    );
    // Baseline = gross + corrections (before savings); the savings band is the
    // gap down to the target budget. The Actual line uses the real signed
    // per-quarter numbers (totalActual).
    const totalBaseline = QUARTERS.map((q) => sum(groups.map((g) => g.baseline[q])));

    // The table aggregates budget / actual / variance across the selected
    // quarters (a row appears if it spends in any of them).
    const sel = selectedQuarters;
    const elapsedSel = sel.filter((qi) => qi < elapsed);
    const aggBudget = (b: number[]) => sum(sel.map((qi) => b[qi]));
    const aggActual = (a: (number | null)[]): number | null =>
        elapsedSel.length === 0
            ? null
            : sum(sel.map((qi) => (a[qi] as number) ?? 0));
    const aggDelta = (b: number[], a: (number | null)[]): number | null => {
        const be = sum(elapsedSel.map((qi) => b[qi]));
        const av = aggActual(a);
        return av !== null && be ? (av - be) / be : null;
    };
    const quarterGroups = groups
        .map((g) => ({
            ...g,
            rows: g.rows
                .filter((r) => aggBudget(r.budget) > 0)
                .sort((a, b) => aggBudget(b.budget) - aggBudget(a.budget)),
        }))
        .filter((g) => g.rows.length > 0);
    const visibleLines = sum(quarterGroups.map((g) => g.rows.length));
    // Editing actuals only makes sense on a single posted quarter.
    const editQ = sel.length === 1 && sel[0] < elapsed ? sel[0] : null;

    const toggle = (seg: Segment) =>
        setExpanded((e) => ({ ...e, [seg]: !e[seg] }));
    const allOpen =
        quarterGroups.length > 0 &&
        quarterGroups.every((g) => expanded[g.segment]);
    const toggleAll = () =>
        setExpanded((e) => {
            const next = { ...e };
            ORDER.forEach((seg) => {
                next[seg] = !allOpen;
            });
            return next;
        });
    const onEdit = (id: string, v: number) => {
        if (editQ !== null)
            setManualActual((m) => ({ ...m, [`${id}:${editQ}`]: v }));
    };

    return (
        <>
            <BudgetTabs />

            <SpendChart
                budget={totalBudget}
                actual={totalActual}
                baseline={totalBaseline}
                elapsed={elapsed}
            />

            <div className={classes.trackBody}>
                <div className={classes.cardCol}>
                    {QUARTERS.map((qi) => (
                        <QuarterCard
                            key={qi}
                            quarter={qi}
                            baseline={totalBaseline[qi]}
                            actual={totalActual[qi]}
                            selected={selectedQuarters.includes(qi)}
                            checked={checkedQuarters.includes(qi)}
                            showCheckbox={
                                selectedQuarters.length > 1 ||
                                !selectedQuarters.includes(qi)
                            }
                            onSelect={selectQuarter}
                            onToggle={toggleQuarter}
                        />
                    ))}
                </div>

                <div className={classes.tableCol}>
                    <div className={`${classes.row} ${classes.colHeaderRow}`}>
                        <div className={classes.nameCell} />
                        <div className={classes.catCell}>
                            <span className={classes.colLabel}>Category</span>
                        </div>
                        <div className={classes.colBudget}>
                            <span className={classes.colLabel}>Baseline</span>
                        </div>
                        <div className={classes.colActual}>
                            <span className={classes.colLabel}>Actual</span>
                        </div>
                        <div className={classes.colDelta}>
                            <span className={classes.colLabel}>vs baseline</span>
                        </div>
                        <div className={classes.colFloor}>
                            <span className={classes.colLabel}>Target</span>
                        </div>
                    </div>

                    <div className={classes.widget}>
                        <div className={`${classes.row} ${classes.tallyRow}`}>
                            <div
                                className={`${classes.nameCell} ${classes.tallyName}`}
                                onClick={toggleAll}
                            >
                                <span className={classes.chev}>
                                    {allOpen ? <DownOutlined /> : <RightOutlined />}
                                </span>
                                <span className={classes.tallyLabel}>All segments</span>
                                <span className={classes.count}>
                                    {visibleLines}{" "}
                                    {visibleLines === 1 ? "line" : "lines"}
                                </span>
                            </div>
                            <div className={classes.catCell} />
                            <QCells
                                baseline={aggBudget(totalBaseline)}
                                actual={aggActual(totalActual)}
                                delta={aggDelta(totalBaseline, totalActual)}
                                target={aggBudget(totalBudget)}
                            />
                        </div>

                        {quarterGroups.length === 0 && (
                            <div className={`${classes.row} ${classes.emptyRow}`}>
                                Nothing lands in the selected quarter
                                {sel.length === 1 ? "" : "s"}.
                            </div>
                        )}

                        {quarterGroups.map((g) => {
                            const open = expanded[g.segment];
                            return (
                                <div key={g.segment} className={classes.section}>
                                    <div
                                        className={`${classes.row} ${classes.sectionRow}`}
                                        onClick={() => toggle(g.segment)}
                                    >
                                        <div className={classes.nameCell}>
                                            <span className={classes.chev}>
                                                {open ? (
                                                    <DownOutlined />
                                                ) : (
                                                    <RightOutlined />
                                                )}
                                            </span>
                                            <span className={classes.segLabel}>
                                                {g.label}
                                            </span>
                                            <span className={classes.count}>
                                                {g.rows.length}{" "}
                                                {g.rows.length === 1 ? "line" : "lines"}
                                            </span>
                                        </div>
                                        <div className={classes.catCell} />
                                        <QCells
                                            baseline={aggBudget(g.baseline)}
                                            actual={aggActual(g.actual)}
                                            delta={aggDelta(g.baseline, g.actual)}
                                            target={aggBudget(g.budget)}
                                        />
                                    </div>

                                    {open &&
                                        g.rows.map((r) => (
                                            <div
                                                key={r.id}
                                                className={`${classes.row} ${classes.lineRow}`}
                                            >
                                                <div className={classes.nameCell}>
                                                    {r.logoUrl ? (
                                                        <img
                                                            className={classes.logo}
                                                            src={r.logoUrl}
                                                            alt=""
                                                            loading="lazy"
                                                        />
                                                    ) : r.categoryKey ? (
                                                        <span
                                                            className={classes.catIcon}
                                                            style={{
                                                                background:
                                                                    CATEGORY_COLOR[
                                                                        r.categoryKey
                                                                    ],
                                                            }}
                                                        >
                                                            {CATEGORY_ICON[r.categoryKey]}
                                                        </span>
                                                    ) : null}
                                                    <span className={classes.lineText}>
                                                        <span className={classes.lineName}>
                                                            {r.name}
                                                        </span>
                                                        {r.sub && (
                                                            <span
                                                                className={classes.lineSub}
                                                            >
                                                                {r.sub}
                                                            </span>
                                                        )}
                                                    </span>
                                                </div>
                                                <div className={classes.catCell}>
                                                    {r.category}
                                                </div>
                                                <QCells
                                                    baseline={aggBudget(r.baseline)}
                                                    actual={aggActual(r.actual)}
                                                    delta={aggDelta(r.baseline, r.actual)}
                                                    target={aggBudget(r.budget)}
                                                    kind={r.kind}
                                                    editable={
                                                        r.editable && editQ !== null
                                                    }
                                                    onEdit={(v) => onEdit(r.id, v)}
                                                />
                                            </div>
                                        ))}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </>
    );
};

/** A selectable quarter card in the negotiations-view style: grey header with
 * the quarter + a checkbox (multi-select), green when selected. Clicking the
 * body selects just that quarter; the checkbox adds/removes more. */
const QuarterCard: React.FC<{
    quarter: number;
    baseline: number;
    actual: number | null;
    selected: boolean;
    checked: boolean;
    showCheckbox: boolean;
    onSelect: (q: number) => void;
    onToggle: (q: number) => void;
}> = ({
    quarter,
    baseline,
    actual,
    selected,
    checked,
    showCheckbox,
    onSelect,
    onToggle,
}) => {
    const delta =
        actual !== null && baseline ? (actual - baseline) / baseline : null;
    return (
        <GenericCard
            className={`${classes.qCard} ${selected ? classes.qCardSel : ""}`}
            type={selected ? "success" : "info"}
            onClick={() => onSelect(quarter)}
        >
            <div className={classes.qCardHead}>
                <span className={classes.qCardQ}>Q{quarter + 1}</span>
                {showCheckbox && (
                    <Checkbox
                        checked={checked}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => onToggle(quarter)}
                    />
                )}
            </div>
            <div className={classes.qCardBody}>
                <div className={classes.qCardStat}>
                    <span className={classes.qCardLabel}>Baseline</span>
                    <span className={classes.qCardVal}>
                        {formatCompact(usd(baseline))}
                    </span>
                </div>
                <div className={classes.qCardStat}>
                    <span className={classes.qCardLabel}>Actual</span>
                    <span className={classes.qCardVal}>
                        {actual === null ? "—" : formatCompact(usd(actual))}
                    </span>
                </div>
                {delta !== null && (
                    <div
                        className={`${classes.qCardPct} ${
                            delta > 0 ? classes.over : classes.under
                        }`}
                    >
                        {formatPct(delta)}
                    </div>
                )}
            </div>
        </GenericCard>
    );
};

const CHART_H = 240;
const CHART_PAD = { l: 52, r: 18, t: 24, b: 22 };

type Pt = { x: number; y: number };

/** Catmull-Rom smoothed path through the points (same style as the reporting
 * view's trend cards). */
const smoothPath = (p: Pt[]): string => {
    if (p.length === 0) return "";
    if (p.length === 1) return `M ${p[0].x} ${p[0].y}`;
    let d = `M ${p[0].x} ${p[0].y}`;
    for (let i = 0; i < p.length - 1; i++) {
        const p0 = p[i - 1] ?? p[i];
        const p1 = p[i];
        const p2 = p[i + 1];
        const p3 = p[i + 2] ?? p2;
        const c1x = p1.x + (p2.x - p0.x) / 6;
        const c1y = p1.y + (p2.y - p0.y) / 6;
        const c2x = p2.x - (p3.x - p1.x) / 6;
        const c2y = p2.y - (p3.y - p1.y) / 6;
        d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`;
    }
    return d;
};

/** Spend chart (Cumulative $ or % vs budget): the baseline budget (before
 * savings), the target budget it nets down to, and the real Actual stopping at
 * TODAY (projected dashed). The gap between baseline and target is the savings. */
const SERIES = [
    { key: "baseline", label: "Baseline budget", short: "Baseline", cls: "sGross" },
    { key: "actual", label: "Actual", short: "Actual", cls: "sRealized" },
    { key: "budget", label: "Target budget", short: "Target", cls: "sBudget" },
] as const;

type ChartView = "cum" | "rel";
const VIEW_OPTIONS = [
    { value: "cum", label: "Cumulative" },
    { value: "rel", label: "vs Baseline" },
];

const SpendChart: React.FC<{
    budget: number[];
    actual: (number | null)[];
    baseline: number[];
    elapsed: number;
}> = ({ budget, actual, baseline, elapsed }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [w, setW] = useState(880);
    const [hover, setHover] = useState<number | null>(null);
    const [view, setView] = useState<ChartView>("cum");
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const ro = new ResizeObserver(() => setW(el.clientWidth));
        ro.observe(el);
        setW(el.clientWidth);
        return () => ro.disconnect();
    }, []);

    const n = QUARTERS.length;
    const plotW = Math.max(1, w - CHART_PAD.l - CHART_PAD.r);
    const plotH = CHART_H - CHART_PAD.t - CHART_PAD.b;
    const baseY = CHART_PAD.t + plotH;
    const x = (i: number) => CHART_PAD.l + (i / (n - 1)) * plotW;

    const cum = (arr: number[]): number[] => {
        let s = 0;
        return arr.map((v) => (s += v));
    };
    const budgetCum = cum(budget);
    const baselineCum = cum(baseline);
    const nPosted = Math.min(Math.max(elapsed, 0), n);
    let aSum = 0;
    const actualCum = actual
        .slice(0, nPosted)
        .map((v) => (aSum += (v as number) ?? 0));

    const band = (t: Pt[], b: Pt[]) =>
        `${smoothPath(t)} ${smoothPath([...b].reverse()).replace(/^M/, "L")} Z`;
    const pctX = (px: number) => `${(px / w) * 100}%`;
    const zoneL = (i: number) => (i === 0 ? CHART_PAD.l : (x(i - 1) + x(i)) / 2);
    const zoneR = (i: number) =>
        i === n - 1 ? w - CHART_PAD.r : (x(i) + x(i + 1)) / 2;
    const showToday = elapsed >= 1 && elapsed < n;
    const todayX = x(Math.max(0, elapsed - 1));
    const signedPct = (p: number) =>
        `${p > 0 ? "+" : p < 0 ? "−" : ""}${Math.abs(p * 100).toFixed(1)}%`;

    const relative = view === "rel";

    // Both views share the same curves/bands — Cumulative shows the cumulative $
    // and vs Budget shows the same as a % of the cumulative budget.
    // The vs-Baseline view measures everything against the baseline (= 0%).
    const baseArr = baselineCum;
    const bArr = budgetCum;
    const aArr = actualCum;
    const relOf = (c: number, q: number) =>
        baseArr[q] ? (c - baseArr[q]) / baseArr[q] : 0;
    const vBaseline = QUARTERS.map((q) => (relative ? 0 : baseArr[q]));
    const vBudget = QUARTERS.map((q) => (relative ? relOf(bArr[q], q) : bArr[q]));
    const vActual = aArr.map((c, q) => (relative ? relOf(c, q) : c));
    const last = nPosted - 1;
    const lastPct =
        nPosted >= 1 && baseArr[last]
            ? (aArr[last] - baseArr[last]) / baseArr[last]
            : 0;
    const projV = (q: number) => (relative ? lastPct : baseArr[q] * (1 + lastPct));

    let yMin: number;
    let yMax: number;
    if (relative) {
        // Baseline is 0% and everything sits at or below it, so anchor 0 near
        // the top and let the axis extend down to the target / actual.
        const lo = Math.min(0, ...vBudget, ...vActual);
        const hi = Math.max(0, ...vActual);
        const pad = (hi - lo) * 0.1 || 0.02;
        yMin = lo - pad;
        yMax = hi + pad;
    } else {
        const proj =
            nPosted < n ? QUARTERS.slice(nPosted).map((q) => projV(q)) : [];
        const allc = [...baseArr, ...bArr, ...aArr, ...proj];
        const hi = Math.max(1, ...allc);
        const lo = Math.min(hi, ...allc);
        const pad = (hi - lo) * 0.08 || 1;
        yMin = Math.max(0, lo - pad);
        yMax = hi + pad;
    }
    const yRange = yMax - yMin || 1;
    const yL = (v: number) => CHART_PAD.t + plotH * (1 - (v - yMin) / yRange);
    const ticksL = [0, 0.25, 0.5, 0.75, 1].map((f) => yMin + f * yRange);
    const axisLabel = (v: number) =>
        relative ? `${Math.round(v * 100)}%` : formatCompact(usd(v));

    const baselinePts = vBaseline.map((v, i) => ({ x: x(i), y: yL(v) }));
    const budgetPts = vBudget.map((v, i) => ({ x: x(i), y: yL(v) }));
    const actualPts = vActual.map((v, i) => ({ x: x(i), y: yL(v) }));
    const projPts: Pt[] =
        nPosted >= 1 && nPosted < n
            ? QUARTERS.slice(last).map((q) => ({ x: x(q), y: yL(projV(q)) }))
            : [];

    // Per-series tooltip value (+ % vs budget) for the hovered quarter.
    const tip = (
        key: string,
        qi: number
    ): { v: number | null; p: number | null } => {
        const v =
            key === "baseline"
                ? baseArr[qi]
                : key === "budget"
                ? bArr[qi]
                : qi < nPosted
                ? aArr[qi]
                : null;
        const p = key === "baseline" ? 0 : v === null ? null : relOf(v, qi);
        return { v, p };
    };

    return (
        <div className={classes.chartCard}>
            <div className={classes.chartHead}>
                <Segmented
                    className={classes.chartViewSel}
                    value={view}
                    onChange={(v) => setView(v as ChartView)}
                    options={VIEW_OPTIONS}
                />
            </div>

            <div className={classes.plot} ref={ref} style={{ height: CHART_H }}>
                <svg width={w} height={CHART_H} className={classes.svg}>
                    {/* gridlines (% or $) */}
                            {ticksL.map((tv, i) => (
                                <g key={i}>
                                    <line
                                        className={classes.gridLine}
                                        x1={CHART_PAD.l}
                                        x2={w - CHART_PAD.r}
                                        y1={yL(tv)}
                                        y2={yL(tv)}
                                    />
                                    <text
                                        className={classes.yLabel}
                                        x={CHART_PAD.l - 8}
                                        y={yL(tv)}
                                        textAnchor="end"
                                        dominantBaseline="middle"
                                    >
                                        {axisLabel(tv)}
                                    </text>
                                </g>
                            ))}

                            {/* Savings band: baseline (before savings) → target */}
                            <path
                                className={classes.bandFloor}
                                d={band(baselinePts, budgetPts)}
                            />
                            {/* Baseline = the reference line (flat 0% in vs-Baseline) */}
                            <g className={classes.sGross}>
                                <path
                                    className={`${classes.line} ${classes.budgetLine}`}
                                    d={smoothPath(baselinePts)}
                                />
                            </g>
                            {/* Target budget line */}
                            <g className={classes.sBudget}>
                                <path
                                    className={classes.line}
                                    d={smoothPath(budgetPts)}
                                />
                            </g>

                            {showToday && (
                                <line
                                    className={classes.todayLine}
                                    x1={todayX}
                                    x2={todayX}
                                    y1={CHART_PAD.t}
                                    y2={baseY}
                                />
                            )}

                            {/* Actual: solid to date, dashed projection after */}
                            {projPts.length >= 2 && (
                                <g className={classes.sRealized}>
                                    <path
                                        className={`${classes.line} ${classes.dashed}`}
                                        d={smoothPath(projPts)}
                                    />
                                </g>
                            )}
                            {actualPts.length >= 2 && (
                                <g className={classes.sRealized}>
                                    <path
                                        className={classes.line}
                                        d={smoothPath(actualPts)}
                                    />
                                </g>
                            )}

                            {/* Hover marker: guide line + a dot on each curve */}
                            {hover !== null && (
                                <g>
                                    <line
                                        className={classes.hoverGuide}
                                        x1={x(hover)}
                                        x2={x(hover)}
                                        y1={CHART_PAD.t}
                                        y2={baseY}
                                    />
                                    {[
                                        {
                                            cls: "sGross",
                                            v: vBaseline[hover] as number | null,
                                        },
                                        { cls: "sBudget", v: vBudget[hover] as number | null },
                                        {
                                            cls: "sRealized",
                                            v: hover < nPosted ? vActual[hover] : null,
                                        },
                                    ]
                                        .filter((d) => d.v !== null)
                                        .map((d, i) => (
                                            <circle
                                                key={i}
                                                className={`${classes.dot} ${classes[d.cls]}`}
                                                cx={x(hover)}
                                                cy={yL(d.v as number)}
                                                r={3.5}
                                            />
                                        ))}
                                </g>
                            )}

                    {/* TODAY badge (both views) */}
                    {showToday && (
                        <g>
                            <rect
                                className={classes.todayBadge}
                                x={todayX - 23}
                                y={3}
                                width={46}
                                height={16}
                                rx={4}
                            />
                            <text
                                className={classes.todayBadgeText}
                                x={todayX}
                                y={14}
                                textAnchor="middle"
                            >
                                TODAY
                            </text>
                        </g>
                    )}

                    {/* X-axis quarter labels (both views) */}
                    {QUARTERS.map((qi) => (
                        <text
                            key={qi}
                            className={classes.xLabel}
                            x={x(qi)}
                            y={CHART_H - 6}
                            textAnchor="middle"
                        >
                            Q{qi + 1}
                        </text>
                    ))}
                </svg>

                {QUARTERS.map((qi) => (
                    <Tooltip
                        key={qi}
                        title={
                            <div className={classes.qtip}>
                                <div className={classes.qtipHead}>
                                    {relative
                                        ? `vs baseline · end of Q${qi + 1}`
                                        : `Cumulative · end of Q${qi + 1}`}
                                </div>
                                {SERIES.map((s) => {
                                    const { v, p } = tip(s.key, qi);
                                    return (
                                        <div key={s.key} className={classes.qtipRow}>
                                            <span
                                                className={`${classes.qtipSwatch} ${classes[s.cls]}`}
                                            />
                                            <span className={classes.qtipName}>
                                                {s.short}
                                            </span>
                                            <span className={classes.qtipVal}>
                                                {v === null
                                                    ? "—"
                                                    : formatCompact(usd(v))}
                                            </span>
                                            <span className={classes.qtipPct}>
                                                {v !== null &&
                                                p !== null &&
                                                s.key !== "baseline"
                                                    ? signedPct(p)
                                                    : ""}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        }
                    >
                        <div
                            className={classes.hoverZone}
                            style={{
                                left: pctX(zoneL(qi)),
                                width: pctX(zoneR(qi) - zoneL(qi)),
                            }}
                            onMouseEnter={() => setHover(qi)}
                            onMouseLeave={() => setHover(null)}
                        />
                    </Tooltip>
                ))}
            </div>

            <div className={classes.legend}>
                {SERIES.map((s) => (
                    <span key={s.key} className={classes.legendItem}>
                        <span
                            className={`${classes.legendSwatch} ${classes[s.cls]}`}
                        />
                        {s.label}
                    </span>
                ))}
            </div>
        </div>
    );
};

/** Budget · Actual · Δ cells for one line. `delta` can be passed explicitly (the
 * year view shows budget for the full year but the to-date variance). */
const QCells: React.FC<{
    baseline: number;
    actual: number | null;
    target: number;
    kind?: ActualKind;
    editable?: boolean;
    onEdit?: (v: number) => void;
    delta?: number | null;
}> = ({ baseline, actual, target, kind, editable, onEdit, delta: deltaProp }) => {
    const delta =
        deltaProp !== undefined
            ? deltaProp
            : actual !== null && baseline
            ? (actual - baseline) / baseline
            : null;
    const actualEl =
        actual === null ? null : editable ? (
            <InputNumber
                size="small"
                bordered={false}
                className={classes.actualInput}
                value={Math.round(actual / 1000)}
                min={0}
                step={10}
                controls={false}
                onChange={(v) => onEdit?.((typeof v === "number" ? v : 0) * 1000)}
                formatter={(v) => {
                    const k = Number(v);
                    if (v === undefined || !Number.isFinite(k)) return "";
                    return Math.abs(k) >= 1000
                        ? `$${(k / 1000).toFixed(1).replace(/\.0$/, "")}M`
                        : `$${k}K`;
                }}
                parser={(v) => {
                    const s = (v ?? "").toString();
                    const num = parseFloat(s.replace(/[^0-9.]/g, "")) || 0;
                    return Math.round(/m/i.test(s) ? num * 1000 : num);
                }}
            />
        ) : kind ? (
            <Tooltip title={SOURCE_LABEL[kind]}>
                <span>{formatCompact(usd(actual))}</span>
            </Tooltip>
        ) : (
            formatCompact(usd(actual))
        );

    return (
        <>
            <div className={classes.colBudget}>
                {baseline > 0 ? formatCompact(usd(baseline)) : ""}
            </div>
            <div className={classes.colActual}>{actualEl}</div>
            <div className={classes.colDelta}>
                {delta === null ? null : (
                    <span className={delta > 0 ? classes.over : classes.under}>
                        {formatPct(delta)}
                    </span>
                )}
            </div>
            <div className={classes.colFloor}>
                {target > 0 ? formatCompact(usd(target)) : ""}
            </div>
        </>
    );
};
